import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getAvailableGames } from '../../utils/collectionAdapters';
import {
  recentLaunchedGamesSelector,
  recentSearchedGamesSelector,
} from '../../redux/reducers/app/persistStorage';
import { useQueryCategories } from './useQueryCategories';
import { useQueryProviders } from './useQueryProviders';
import { useQueryGamesByIds } from './useQueryGames';
import { useRecentGames } from '../useRecentGames';

const useQueryCommon = (games = [], queryOptions = {}) => {
  const queryCategories = useQueryCategories({ _limit: -1 }, queryOptions);
  const queryProviders = useQueryProviders({ _limit: -1 }, queryOptions);

  const isLoading = queryCategories.isLoading || queryProviders.isLoading;
  const isSuccess = queryCategories.isSuccess && queryProviders.isSuccess;

  const data = useMemo(() => {
    return isSuccess
      ? getAvailableGames({
          games,
          categories: queryCategories.data?.items,
          providers: queryProviders.data?.items,
        })
      : games;
  }, [games, isSuccess, queryCategories.data, queryProviders.data]);

  return {
    isLoading,
    isSuccess,
    data,
  };
};

export const useQueryRecentSearchedGames = (queryOptions = {}) => {
  const games = useSelector(recentSearchedGamesSelector);
  return useQueryCommon(games, queryOptions);
};

const gameIdValidator = (id) => typeof id === 'string';

export const useQueryRecentLaunchedGames = (queryOptions = {}) => {
  const games = useSelector(recentLaunchedGamesSelector);
  const ids = games?.map((game) => game.id).filter(gameIdValidator);

  const select = (data) => {
    return data?.items?.filter((game) => game?.status === 'ACTIVE') || [];
  };

  return useQueryGamesByIds(ids, { select, ...queryOptions });
};

export const useQueryRecentGame = (queryOptions = {}) => {
  const { data, ...params } = useRecentGames(queryOptions);
  const recentGame = useMemo(() => data?.[0] ?? null, [data]);

  return {
    ...params,
    data: recentGame,
  };
};
